import { useInfiniteQuery } from 'react-query';
import { EisScoredFieldsResponse } from '../_types';
import {
  performanceReportingApiUrl,
  fetcher,
  handleError,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';

export type EisScoredFieldsParams = {
  ruleVersionId?: number;
  search?: string;
  dataType?: string;
  status?: string;
  sort?: string;
  nextToken?: string;
} & Record<string, unknown>;

export const getEisScoredFields = async (
  ruleId: number,
  params: EisScoredFieldsParams = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void,
): Promise<EisScoredFieldsResponse> => {
  try {
    const baseUrl = `${performanceReportingApiUrl()}/performance/eis/v1/rules/${ruleId}/indicators`;
    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisScoredFields = (
  ruleId: number,
  params: EisScoredFieldsParams = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void,
) => {
  return useInfiniteQuery<EisScoredFieldsResponse, ServerErrorResponse>(
    ['eisScoredFields', ruleId, JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return getEisScoredFields(ruleId, combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      enabled: !!ruleId,
      ...options,
    },
  );
};
